import React, { useEffect, useState } from "react";
import { Bell, Loader2, X } from "lucide-react";

import { updateSavedSearch } from "../../../services/savedSearchesApi";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";

// Matches the days input's own min/max — clamping here means a blank, zero,
// negative or out-of-range value never reaches the backend's own
// PositiveSmallIntegerField validation instead of silently landing on 14.
const clampSkipDays = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 14;
  return Math.min(90, Math.max(1, Math.round(n)));
};

// Edits an *existing* saved search's alert settings — the save modal only
// sets these once, at creation. Same email/frequency controls as there,
// plus the two this project's own plan called for and the save modal never
// exposed: an in-app toggle independent of email, and "skip anything closing
// too soon to bid well."
export default function AlertSettingsModal({ open, onClose, savedSearch, onSaved }) {
  const [alertEmail, setAlertEmail] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [alertInapp, setAlertInapp] = useState(true);
  const [skipOn, setSkipOn] = useState(false);
  const [skipDays, setSkipDays] = useState(14);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !savedSearch) return;
    setAlertEmail(savedSearch.alert_email);
    setFrequency(savedSearch.alert_frequency === "weekly" ? "weekly" : "daily");
    setAlertInapp(savedSearch.alert_inapp);
    setSkipOn(Boolean(savedSearch.skip_closing_within_days));
    setSkipDays(savedSearch.skip_closing_within_days || 14);
  }, [open, savedSearch]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open || !savedSearch) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateSavedSearch(savedSearch.id, {
        alert_email: alertEmail,
        alert_frequency: alertEmail ? frequency : "off",
        alert_inapp: alertInapp,
        skip_closing_within_days: skipOn ? clampSkipDays(skipDays) : null,
      });
      showSuccessToast("Alert settings saved");
      onSaved?.(updated);
      onClose?.();
    } catch {
      showErrorToast("Could not save these settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const Switch = ({ on, onToggle }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className={`w-9 h-[21px] rounded-full relative shrink-0 transition-colors ${
        on ? "bg-[#A04A97]" : "bg-[#D8D3DB]"
      }`}
    >
      <span
        className={`absolute top-[2px] w-[17px] h-[17px] rounded-full bg-white transition-all ${
          on ? "left-[17px]" : "left-[2px]"
        }`}
      />
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 backdrop-blur-[1px]"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] font-outfit border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 flex items-start gap-3">
          <span className="w-[34px] h-[34px] rounded-[9px] bg-[#FDF6FC] grid place-items-center shrink-0">
            <Bell className="w-[16px] h-[16px] text-[#BB42AE]" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-[17px] font-semibold text-[#18101C]">Alerts</h2>
            <p className="text-[13px] text-[#71717A] mt-0.5 truncate">for {savedSearch.name}</p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            className="text-[#9CA3AF] hover:text-[#52525B] shrink-0"
            aria-label="Close"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="px-6 pt-5 flex flex-col gap-3">
          <div className="border border-[#E5E7EB] rounded-[10px] p-3.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch on={alertEmail} onToggle={() => setAlertEmail((v) => !v)} />
              <div className="flex-1">
                <span className="block text-[14px] font-medium text-[#18101C]">Email alerts</span>
                <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
                  A summary email of new matches. Not instant, since tenders only update once a day.
                </span>
              </div>
            </label>
            {alertEmail && (
              <div className="flex gap-1.5 mt-3 pl-12">
                {[["daily", "Daily"], ["weekly", "Weekly"]].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFrequency(value)}
                    className={`text-[13px] font-semibold rounded-md px-3.5 py-1.5 border transition-colors ${
                      frequency === value
                        ? "text-white bg-[#A04A97] border-[#A04A97]"
                        : "text-[#52525B] bg-white border-[#00000024] hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border border-[#E5E7EB] rounded-[10px] p-3.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch on={alertInapp} onToggle={() => setAlertInapp((v) => !v)} />
              <div className="flex-1">
                <span className="block text-[14px] font-medium text-[#18101C]">Notify me in DocuSensa</span>
                <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
                  A bell notification each time a digest goes out, on top of the email.
                </span>
              </div>
            </label>
          </div>

          <div className="border border-[#E5E7EB] rounded-[10px] p-3.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch on={skipOn} onToggle={() => setSkipOn((v) => !v)} />
              <div className="flex-1">
                <span className="block text-[14px] font-medium text-[#18101C]">Skip tenders closing soon</span>
                <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
                  Leave out anything closing too soon to bid well.
                </span>
              </div>
            </label>
            {skipOn && (
              <div className="flex items-center gap-2 mt-3 pl-12">
                <span className="text-[13px] text-[#52525B]">Closing within</span>
                <input
                  id="alert-skip-days"
                  type="number"
                  min="1"
                  max="90"
                  value={skipDays}
                  onChange={(e) => setSkipDays(e.target.value)}
                  className="w-[60px] h-[30px] font-outfit text-[13px] text-center border border-[#00000024]
                             rounded-md outline-none focus:border-[#C95EBE]"
                />
                <span className="text-[13px] text-[#52525B]">days</span>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="px-4 py-2 rounded-md text-[14px] font-medium text-[#52525B] hover:bg-gray-100 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-[14px] font-semibold text-white
                       bg-[#A04A97] hover:bg-[#8f4188] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save alerts"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
